// @ts-nocheck
import type { ValidationIssue } from '../../diagnostics';
import type { PropertyDefinition } from '../../types/canonical';
import { normalizeListForStorage } from '../variables/listValueUtils';
import type { CoercionPolicy } from './types';

/**
 * Normalizes a typed runtime value into a canonical storage string for the given property.
 * Returns the string to store and any issues encountered.
 * Returns null for storedValue when the entry should be removed (empty/unset).
 * Never throws.
 */
export function normalizeWriteValue(
  typedValue: unknown,
  definition: PropertyDefinition,
  policy: CoercionPolicy,
): { storedValue: string | null; issues: ValidationIssue[] } {
  const issues: ValidationIssue[] = [];

  // list: null/undefined → empty array (persist "[]"), matching item-variable list semantics
  if (definition.type === 'list') {
    const coerced = normalizeListForStorage(typedValue);
    if (coerced === null) {
      issues.push({
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Cannot serialize list for property "${definition.id}": value must be a JSON-serializable array within size limits.`,
        path: definition.id,
      });
      return { storedValue: null, issues };
    }
    return { storedValue: JSON.stringify(coerced), issues };
  }

  // null/undefined always removes the entry
  if (typedValue === null || typedValue === undefined) {
    return { storedValue: null, issues };
  }

  switch (definition.type) {
    case 'text': {
      let str = String(typedValue);
      if (policy.trimTextOnWrite) {
        str = str.trim();
      }
      return { storedValue: str.length === 0 ? null : str, issues };
    }

    case 'multiline': {
      let str = String(typedValue);
      if (policy.trimTextOnWrite) {
        // Trim trailing whitespace per line but preserve newlines
        str = str
          .split('\n')
          .map(line => line.trimEnd())
          .join('\n')
          .trim();
      }
      return { storedValue: str.length === 0 ? null : str, issues };
    }

    case 'url':
    case 'email':
    case 'phone':
    case 'color': {
      let str = String(typedValue);
      if (policy.trimTextOnWrite) {
        str = str.trim();
      }
      return { storedValue: str.length === 0 ? null : str, issues };
    }

    case 'integer': {
      const num = typeof typedValue === 'number' ? typedValue : parseFloat(String(typedValue));
      if (isNaN(num)) {
        return { storedValue: null, issues };
      }
      return { storedValue: String(Math.round(num)), issues };
    }

    case 'float': {
      const num = typeof typedValue === 'number' ? typedValue : parseFloat(String(typedValue));
      if (isNaN(num)) {
        return { storedValue: null, issues };
      }
      return { storedValue: String(num), issues };
    }

    case 'boolean': {
      if (typeof typedValue === 'boolean') {
        return { storedValue: typedValue ? 'true' : 'false', issues };
      }
      const str = String(typedValue).toLowerCase();
      if (str === 'true' || str === '1' || str === 'yes') {
        return { storedValue: 'true', issues };
      }
      if (str === 'false' || str === '0' || str === 'no') {
        return { storedValue: 'false', issues };
      }
      // Unrecognized boolean-like string — remove entry
      return { storedValue: null, issues };
    }

    case 'date':
    case 'datetime': {
      const str = String(typedValue).trim();
      return { storedValue: str.length === 0 ? null : str, issues };
    }

    case 'enum': {
      const str = String(typedValue).trim();
      if (str.length === 0) {
        return { storedValue: null, issues };
      }
      const enumValues = definition.validation?.enumValues ?? [];
      const match = enumValues.find((ev: { id: string }) => ev.id === str);
      if (!match && policy.strictEnumValues) {
        issues.push({
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Value "${str}" is not a valid enum option for property "${definition.id}".`,
          path: definition.id,
        });
        return { storedValue: null, issues };
      }
      return { storedValue: str, issues };
    }

    case 'image': {
      let str = String(typedValue);
      if (policy.trimTextOnWrite) {
        str = str.trim();
      }
      return { storedValue: str.length === 0 ? null : str, issues };
    }

    case 'json': {
      try {
        const serialized = JSON.stringify(typedValue);
        // null/undefined JSON representations → remove entry
        if (serialized === 'null' || serialized === undefined) {
          return { storedValue: null, issues };
        }
        return { storedValue: serialized, issues };
      } catch {
        issues.push({
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Cannot serialize JSON value for property "${definition.id}": value is not JSON-serializable.`,
          path: definition.id,
        });
        return { storedValue: null, issues };
      }
    }

    case 'table':
    case 'fileTable': {
      const str = typedValue === null || typedValue === undefined ? '' : String(typedValue);
      return { storedValue: str.length === 0 ? null : str, issues };
    }

    default: {
      // Exhaustive fallback — convert to string
      const str = String(typedValue);
      return { storedValue: str.length === 0 ? null : str, issues };
    }
  }
}
